import React, { useState, useEffect, forwardRef } from "react";
import {
  createMarketingSKU,
  createDependentMarketingSKU,
  updateDependentMarketingSKU,
  updateMarketingSKU,
  getMarketingSKUById,
  getDependentMarketingSKUs,
} from "../../core/api/marketing";
import {
  getProductPricingByProductVariation,
  getProductTypes,
  getProductsByProductType,
  getProductVariationsByProductId,
} from "../../core/api/products";
import { fetchBrandsBySelectedClients } from "../../core/api/settings";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useSelector } from "react-redux";
import {
  Card,
  Select,
  Form,
  Button,
} from "antd";
import { Row, Col } from "antd";
import IndependentSKULayout from "./components/independentsku/IndependentSKULayout";
import MarketingSKUForm from "./components/independentsku/MarketingSKUForm";
import DependentSKUEditor from "./components/independentsku/DependentSKUEditor";
import SKUItemsTable from "./components/independentsku/SKUItemsTable";

const IndependentSKU = forwardRef(
  ({ isVisible, onClose, skuId, onResize, clientId }, ref) => {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [offerSKU, setOfferSKU] = useState({
      skuCode: null,
      items: [],
      isBundleSKU: false,
      total_bundle_price: 0,
      bundle_discount: 0,
      bundle_price_to_pay: 0,
      bundle_rule: "",
    });
    // We need this data for the layout component to position correctly
    const headerState = useSelector((state) => state.toggle_header);
    const [existingDependentSKUs, setExistingDependentSKUs] = useState([]);
    const [selectedDependentSKUs, setSelectedDependentSKUs] = useState([]);
    const MySwal = withReactContent(Swal);
    const [addNewOrSelect, setAddNewOrSelect] = useState(1);
    const selectedClients = useSelector((state) => state.selectedClients);
    const [editingItem, setEditingItem] = useState({
      id: null,
      dependent_sku: "",
      product_code: "",
      product_pricing: null,
      qty: 1,
      qtyDetailed: "1",
      category: null,
      category_name: "",
      description: "",
      unit_description: "",
      originalPrice: 0,
      discount: 0,
      finalPrice: 0,
      optional: "No",
      rule: "",
    });
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [productVariationOptions, setProductVariationOptions] = useState([]);
    const [productPricingOptions, setProductPricingOptions] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [brands, setBrands] = useState([]);

    // Helper function to safely convert to number
    const toNumber = (value) => {
      if (typeof value === 'number') return value;
      const num = parseFloat(value);
      return isNaN(num) ? 0 : num;
    };

    const resetState = () => {
      setOfferSKU({
        skuCode: null,
        items: [],
        isBundleSKU: false,
        total_bundle_price: 0,
        bundle_discount: 0,
        bundle_price_to_pay: 0,
        bundle_rule: "",
      });

      setExistingDependentSKUs([]);
      setSelectedDependentSKUs([]);
      setAddNewOrSelect(1);
      setEditingItem({
        id: null,
        dependent_sku: "",
        product_code: "",
        product_pricing: null,
        qty: 1,
        qtyDetailed: "1",
        category: null,
        category_name: "",
        description: "",
        unit_description: "",
        originalPrice: 0,
        discount: 0,
        finalPrice: 0,
        optional: "No",
        rule: "",
        product: null,
      });
      setCategoryOptions([]);
      setProductOptions([]);
      setProductVariationOptions([]);
      setProductPricingOptions([]);
      setIsSaving(false);
    };

    useEffect(() => {
      const fetchBrands = async () => {
        try {
          const response = await fetchBrandsBySelectedClients(selectedClients);
          if (response.status === 200) {
            const brandData = response.data;
            setBrands(
              brandData.map((item) => ({
                value: item.id,
                label: item.brand_name,
              }))
            );
          }
        } catch (error) {
          console.error("Error fetching brands:", error);
        }
      };

      fetchBrands();
    }, [clientId]);

    useEffect(() => {
      const fetchExistingDependentSKUs = async () => {
        try {
          const response = await getDependentMarketingSKUs();
          setExistingDependentSKUs(
            response.data.map((sku) => ({
              value: sku.id,
              label: `${sku.product_code} => ${sku.description} => Price: $${sku.original_price} => Discount: $${sku.discount} => Price to Pay: $${sku.final_price}`,
              obj: sku,
            }))
          );
        } catch (error) {
          console.error("Error fetching existing dependent SKUs:", error);
        }
      };
      if (isVisible) fetchExistingDependentSKUs();
    }, [isVisible]);

    useEffect(() => {
      const fetchMarketingSKU = async () => {
        if (skuId) {
          try {
            const response = await getMarketingSKUById(skuId);
            const sku = response.data;
            setOfferSKU({
              skuCode: sku.sku,
              brand: sku.brand,
              isBundleSKU: sku.is_bundle,
              total_bundle_price: sku.total_bundle_price || 0,
              bundle_discount: sku.bundle_discount || 0,
              bundle_price_to_pay: sku.bundle_price_to_pay || 0,
              bundle_rule: sku.bundle_rule || "",
              items: sku.marketing_dependent_skus_details.map((item) => ({
                id: item.id,
                dependent_sku: item.sku,
                product_code: item.product_code,
                product_pricing_sku: item.product_pricing_sku,
                product_pricing: item.product_pricing,
                qty: item.quantity,
                qtyDetailed: item.quantity_detailed,
                category: item.product_type_id,
                category_name: item.product_type,
                description: item.description,
                unit_description: item.unit_description,
                originalPrice: item.original_price,
                discount: item.discount,
                finalPrice: item.final_price,
                optional: item.optional,
                rule: item.rule,
                variation: item.product_variation,
                variation_code: item.variation_code,
              })),
            });
          } catch (error) {
            console.error("Error fetching offer pricing details:", error);
          }
        }
      };

      fetchMarketingSKU();
    }, [skuId]);

    useEffect(() => {
      const fetchProductCategories = async () => {
        try {
          const categories = await getProductTypes();
          setCategoryOptions(
            categories.data.map((category) => ({
              value: category.id,
              label: category.name,
            }))
          );
        } catch (error) {
          console.error("Error fetching product categories:", error);
        }
      };
      fetchProductCategories();
    }, [isVisible]);

    const fetchProductPricingSKUs = async (productVariationId) => {
      try {
        const response = await getProductPricingByProductVariation(
          productVariationId
        );
        const options = response.data.map((pricing) => ({
          value: pricing.id,
          label: pricing.sku,
          original_price: pricing.original_price,
        }));
        setProductPricingOptions(options);
        if (options.length === 1) {
          const selectedPricing = options[0];
          setEditingItem((prev) => ({
            ...prev,
            product_pricing: selectedPricing.value,
            product_pricing_sku: selectedPricing.label,
            originalPrice: toNumber(selectedPricing.original_price) * toNumber(prev.qty),
            finalPrice: toNumber(selectedPricing.original_price) * toNumber(prev.qty),
          }));
        }
      } catch (error) {
        console.error("Error fetching product pricing SKUs:", error);
        setProductPricingOptions([]);
      }
    };

    const handleBrandChange = async (selectedBrand) => {
      const brand = brands.find((brand) => brand.value === selectedBrand);
      setOfferSKU({
        ...offerSKU,
        brand: selectedBrand,
      });
    };

    const handleCategoryChange = async (selectedCategory) => {
      const category = categoryOptions.find(
        (category) => category.value === selectedCategory
      );
      setEditingItem({
        ...editingItem,
        category: selectedCategory,
        category_name: category?.label,
        variation: null,
        product_pricing: null,
        product_pricing_sku: null,
        originalPrice: 0,
        unit_description: "",
        product: null,
      });
      setProductOptions([]);
      setProductVariationOptions([]);
      setProductPricingOptions([]);

      try {
        const response = await getProductsByProductType(selectedCategory);
        if (response.status === 200) {
          setProductOptions(
            response.data.map((product) => ({
              value: product.id,
              label: product.name,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    const handleProductChange = async (selectedProduct) => {
      const product = productOptions.find(
        (product) => product.value === selectedProduct
      );
      setEditingItem({
        ...editingItem,
        product: selectedProduct,
        product_code: product?.product_code,
        variation: null,
        product_pricing: null,
        product_pricing_sku: null,
        originalPrice: 0,
        unit_description: "",
      });
      setProductVariationOptions([]);
      setProductPricingOptions([]);
      try {
        const variations = await getProductVariationsByProductId(selectedProduct);
        setProductVariationOptions(
          variations.data.map((variation) => ({
            value: variation.id,
            label: variation.product_variation_desc,
            product_code: variation.product_code,
            variation_code: variation.variation_code,
          }))
        );
      } catch (error) {
        console.error("Error fetching product variations:", error);
      }
    };

    const handleSaveItem = (item) => {
      const updatedItems = [...offerSKU.items, { 
        ...item,
        originalPrice: toNumber(item.originalPrice),
        discount: toNumber(item.discount),
        finalPrice: toNumber(item.finalPrice)
      }];
      if (offerSKU.isBundleSKU) {
        setOfferSKU((prev) => {
          const newOfferSKU = { ...prev, items: updatedItems };
          return newOfferSKU;
        });
      } else {
        setOfferSKU((prev) => {
          const newOfferSKU = { ...prev, items: updatedItems };
          handleSaveSKU(newOfferSKU);
          return newOfferSKU;
        });
      }
      setEditingItem({
        id: null,
        dependent_sku: "",
        product_code: "",
        product_pricing: null,
        qty: 1,
        qtyDetailed: "1",
        category: null,
        category_name: "",
        description: "",
        unit_description: "",
        originalPrice: 0,
        discount: 0,
        finalPrice: 0,
        optional: "No",
        rule: "",
      });
    };

    const handleSave = () => {
      handleSaveSKU(offerSKU);
    };

    const handleSaveSKU = async (updatedOfferSKU) => {
      setIsSaving(true);
      try {
        if (!updatedOfferSKU.skuCode) {
          MySwal.fire("Warning", "Please enter an Offer SKU.", "warning");
          setIsSaving(false);
          return;
        }
        if (updatedOfferSKU.items.length === 0) {
          MySwal.fire("Warning", "Please add at least one item.", "warning");
          setIsSaving(false);
          return;
        }
        for (const item of updatedOfferSKU.items) {
          if (!item.variation) {
            MySwal.fire(
              "Warning",
              "Product variation is missing for one or more items.",
              "warning"
            );
            setIsSaving(false);
            return;
          }
          if (!item.qty || item.qty <= 0) {
            MySwal.fire(
              "Warning",
              "Quantity must be greater than zero for all items.",
              "warning"
            );
            setIsSaving(false);
            return;
          }
        }
        const dependentSKUIds = [];
        for (const item of updatedOfferSKU.items) {
          if (item.id) {
            const res = await updateDependentMarketingSKU(item.id, {
              sku: item.dependent_sku,
              quantity: item.qty,
              quantity_detailed: item.qtyDetailed,
              original_price: toNumber(item.originalPrice),
              discount: toNumber(item.discount),
              final_price: toNumber(item.finalPrice),
              product_variation: item.variation,
              description: item.description,
              optional: item.optional,
              product_pricing: item.product_pricing,
              unit_description: item.unit_description,
              rule: item.rule,
            });
            dependentSKUIds.push(res.data.id);
          } else {
            const res = await createDependentMarketingSKU({
              sku: item.dependent_sku,
              quantity: item.qty,
              quantity_detailed: item.qtyDetailed,
              original_price: toNumber(item.originalPrice),
              discount: toNumber(item.discount),
              final_price: toNumber(item.finalPrice),
              product_variation: item.variation,
              description: item.description,
              optional: item.optional,
              product_pricing: item.product_pricing,
              unit_description: item.unit_description,
              rule: item.rule,
            });
            dependentSKUIds.push(res.data.id);
          }
        }
        if (!skuId) {
          try {
            await createMarketingSKU({
              sku: updatedOfferSKU.skuCode,
              brand: updatedOfferSKU.brand,
              is_bundle: updatedOfferSKU.isBundleSKU,
              total_bundle_price: toNumber(updatedOfferSKU.total_bundle_price),
              bundle_discount: toNumber(updatedOfferSKU.bundle_discount),
              bundle_price_to_pay: toNumber(updatedOfferSKU.bundle_price_to_pay),
              bundle_rule: updatedOfferSKU.bundle_rule,
              marketing_dependent_skus: dependentSKUIds,
            });
          } catch (error) {
            if (
              error.response &&
              error.response.data &&
              error.response.data.sku &&
              error.response.data.sku[0] ===
                "Marketing SKU with this sku already exists."
            ) {
              MySwal.fire("Error", "Marketing SKU already exists.", "error");
              setIsSaving(false);
              return;
            } else {
              throw error;
            }
          }
        } else {
          try {
            await updateMarketingSKU(skuId, {
              sku: updatedOfferSKU.skuCode,
              brand: updatedOfferSKU.brand,
              is_bundle: updatedOfferSKU.isBundleSKU,
              total_bundle_price: toNumber(updatedOfferSKU.total_bundle_price),
              bundle_discount: toNumber(updatedOfferSKU.bundle_discount),
              bundle_price_to_pay: toNumber(updatedOfferSKU.bundle_price_to_pay),
              bundle_rule: updatedOfferSKU.bundle_rule,
              marketing_dependent_skus: dependentSKUIds,
            });
          } catch (error) {
            if (
              error.response &&
              error.response.data &&
              error.response.data.sku &&
              error.response.data.sku[0] ===
                "Marketing SKU with this sku already exists."
            ) {
              MySwal.fire("Error", "Marketing SKU already exists.", "error");
              setIsSaving(false);
              return;
            } else {
              throw error;
            }
          }
        }

        MySwal.fire("Success", "Marketing SKU saved successfully!", "success");
        onClose();
        resetState();
      } catch (error) {
        console.error("Error saving Offer Pricing:", error);
        MySwal.fire("Error", "Failed to save Marketing SKU.", "error");
      } finally {
        setIsSaving(false);
      }
    };

    useEffect(() => {
      const sidebarWidth = isFullScreen ? window.innerWidth * 0.6 : 555;
      onResize(sidebarWidth);
    }, [isFullScreen, onResize]);

    const handleSaveSelectedSKUs = () => {
      const newItems = selectedDependentSKUs.map((sku) => ({
        id: sku.obj.id,
        dependent_sku: sku.obj.sku,
        product_code: "",
        product_pricing: null,
        qty: toNumber(sku.obj.quantity),
        qtyDetailed: sku.obj.quantity_detailed,
        category: null,
        variation: sku.obj.product_variation,
        variation_code: sku.obj.variation_code,
        category_name: sku.obj.product_type,
        description: sku.obj.description,
        unit_description: sku.obj.unit_description,
        originalPrice: toNumber(sku.obj.original_price),
        discount: toNumber(sku.obj.discount),
        finalPrice: toNumber(sku.obj.final_price),
        optional: "No",
        rule: "-",
        product_pricing_sku: sku.obj.product_pricing_sku,
      }));
      setOfferSKU((prevState) => ({
        ...prevState,
        items: [...prevState.items, ...newItems],
      }));
      setSelectedDependentSKUs([]);
    };

    const handleSelectChange = (values) => {
      const selectedSKUs = values.map((value) =>
        existingDependentSKUs.find((sku) => sku.value === value)
      );
      setSelectedDependentSKUs(selectedSKUs);
    };

    useEffect(() => {
      const totalBundlePrice = offerSKU.items.reduce(
        (total, item) => total + toNumber(item.originalPrice),
        0
      );
      const totalBundleDiscount = offerSKU.items.reduce(
        (total, item) => total + toNumber(item.discount),
        0
      );
      setOfferSKU((prev) => ({
        ...prev,
        total_bundle_price: totalBundlePrice,
        bundle_discount: totalBundleDiscount,
        bundle_price_to_pay: totalBundlePrice - totalBundleDiscount,
      }));
    }, [offerSKU.items]);

    const handleBundleDiscountChange = (e) => {
      const discount = toNumber(e.target.value);
      setOfferSKU((prev) => ({
        ...prev,
        bundle_discount: discount,
        bundle_price_to_pay: toNumber(prev.total_bundle_price) - discount,
      }));
    };

    const handleBundleRuleChange = (e) => {
      setOfferSKU((prev) => ({
        ...prev,
        bundle_rule: e.target.value,
      }));
    };

    return (
      <IndependentSKULayout
        isVisible={isVisible}
        isFullScreen={isFullScreen}
        setIsFullScreen={setIsFullScreen}
        skuId={skuId}
        offerSKU={offerSKU}
        onClose={() => {
          setEditingItem(null);
          setOfferSKU({ skuCode: null, items: [] });
          onClose();
          resetState();
        }}
        headerState={headerState}
      >
        <Form layout="vertical">
          <MarketingSKUForm
            offerSKU={offerSKU}
            setOfferSKU={(newOfferSKU) => {
              setOfferSKU(newOfferSKU);
              if (!newOfferSKU.isBundleSKU && newOfferSKU.skuCode !== offerSKU.skuCode) {
                setEditingItem({
                  ...editingItem,
                  dependent_sku: newOfferSKU.skuCode?.toUpperCase(),
                });
              }
            }}
            skuId={skuId}
            brands={brands}
            handleBrandChange={handleBrandChange}
            addNewOrSelect={addNewOrSelect}
            setAddNewOrSelect={setAddNewOrSelect}
          />

          <DependentSKUEditor
            editingItem={editingItem}
            setEditingItem={setEditingItem}
            offerSKU={{...offerSKU, addNewOrSelect}}
            skuId={skuId}
            categoryOptions={categoryOptions}
            productOptions={productOptions}
            productVariationOptions={productVariationOptions}
            productPricingOptions={productPricingOptions}
            handleCategoryChange={handleCategoryChange}
            handleProductChange={handleProductChange}
            handleSaveItem={handleSaveItem}
            toNumber={toNumber}
            fetchProductPricingSKUs={fetchProductPricingSKUs}
          />
          <Card
            size="small"
            hidden={
              !offerSKU.isBundleSKU ||
              (offerSKU.isBundleSKU && addNewOrSelect === 1)
            }
          >
            <Form layout="vertical" hidden={skuId}>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="Select From Existing Marketing Dependent SKUs">
                    <Select
                      allowClear
                      showSearch
                      mode="multiple"
                      options={existingDependentSKUs}
                      value={selectedDependentSKUs}
                      onChange={handleSelectChange}
                      style={{ width: "100%" }}
                      filterOption={(input, option) =>
                        option.label
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item>
                    <Button type="primary" onClick={handleSaveSelectedSKUs}>
                      Save
                    </Button>
                    <Button
                      onClick={() => setSelectedDependentSKUs([])}
                      style={{ marginLeft: 8 }}
                    >
                      Cancel
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
          <SKUItemsTable
            offerSKU={offerSKU}
            handleBundleDiscountChange={handleBundleDiscountChange}
            handleBundleRuleChange={handleBundleRuleChange}
          />
          <div className="d-flex justify-content-end mt-4">
            <Button
              type="danger"
              onClick={() => {
                setEditingItem(null);
                onClose();
                resetState();
              }}
              style={{ marginRight: 10 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={() => handleSave()}
              disabled={isSaving}
            >
              {isSaving
                ? "Saving..."
                : skuId
                ? "Update Marketing SKU"
                : "Create Marketing SKU"}
            </Button>
          </div>
        </Form>
      </IndependentSKULayout>
    );
  }
);

IndependentSKU.displayName = "IndependentSKU";
export default IndependentSKU;